'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var reactRouterDom = require('react-router-dom');
var index = require('./ClaimBuilder/index.js');
var paths = require('../../paths.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const defaultRouteMapping = {
  ITEM_SELECTION: paths.ITEM_SELECTION,
  ITEM_QUANTITY: paths.ITEM_QUANTITY,
  ITEM_PHOTO: paths.ITEM_PHOTO,
  ITEM_ADDITIONAL_INFO: paths.ITEM_ADDITIONAL_INFO,
  ITEM_CLAIM_SUMMARY: paths.ITEM_CLAIM_SUMMARY,
  PACKAGE_SELECTION: paths.PACKAGE_SELECTION,
  PACKAGE_ADDITIONAL_INFO: paths.PACKAGE_ADDITIONAL_INFO,
  PACKAGE_CLAIM_SUMMARY: paths.PACKAGE_CLAIM_SUMMARY,
  CONFIRMATION: paths.CONFIRMATION,
  ITEM_REMOVE_CONFIRMATION: paths.ITEM_REMOVE_CONFIRMATION,
  PACKAGE_REMOVE_CONFIRMATION: paths.PACKAGE_REMOVE_CONFIRMATION,
  ORDER_LOOKUP: paths.ORDER_LOOKUP
};

/**
 * ClaimNavigator handles navigating through the claim creation process based on events processed by ClaimBuilder
 * Separating the navigation from the builder state allows us to test views in isolation, since
 * we can choose to exclude the ClaimNavigator component from stories / tests.
 */
const ClaimNavigator = _ref => {
  let {
    children,
    routeMapping
  } = _ref;
  const {
    state
  } = index.useClaimBuilder();
  const navigate = reactRouterDom.useNavigate();
  const {
    ITEM_SELECTION,
    ITEM_QUANTITY,
    ITEM_PHOTO,
    ITEM_ADDITIONAL_INFO,
    ITEM_CLAIM_SUMMARY,
    PACKAGE_SELECTION,
    PACKAGE_ADDITIONAL_INFO,
    PACKAGE_CLAIM_SUMMARY,
    CONFIRMATION,
    ITEM_REMOVE_CONFIRMATION,
    PACKAGE_REMOVE_CONFIRMATION,
    ORDER_LOOKUP
  } = {
    ...defaultRouteMapping,
    ...routeMapping
  };
  React.useEffect(() => {
    const {
      claimType,
      event,
      currentItemClaim,
      currentPackageClaim
    } = state;
    if (!event) return;
    const itemClaimStepToPath = {
      [index.ClaimStepId.QUANTITY]: ITEM_QUANTITY,
      [index.ClaimStepId.PHOTOS]: ITEM_PHOTO,
      [index.ClaimStepId.ADDITIONAL_INFO]: ITEM_ADDITIONAL_INFO
    };
    const packageClaimStepToPath = {
      [index.ClaimStepId.ADDITIONAL_INFO]: PACKAGE_ADDITIONAL_INFO
    };
    switch (event) {
      case index.ClaimBuilderEvent.ORDER_LOOKUP_SUCCESS:
        {
          if (index.isItemClaimType(claimType)) {
            navigate(ITEM_SELECTION);
          } else {
            navigate(PACKAGE_SELECTION);
          }
          break;
        }
      case index.ClaimBuilderEvent.CLAIM_ITEM_SELECTED:
        {
          if (claimType === index.ClaimType.PackageMissing) {
            if (!(currentPackageClaim !== null && currentPackageClaim !== void 0 && currentPackageClaim.currentClaimStep)) throw new Error('No currentClaimStep defined. Cannot navigate');
            const step = packageClaimStepToPath[currentPackageClaim.currentClaimStep.id];
            navigate(step);
          } else {
            if (!(currentItemClaim !== null && currentItemClaim !== void 0 && currentItemClaim.currentClaimStep)) throw new Error('No currentClaimStep defined. Cannot navigate');
            const step = itemClaimStepToPath[currentItemClaim.currentClaimStep.id];
            navigate(step);
          }
          break;
        }
      case index.ClaimBuilderEvent.NAVIGATED_BACK:
        {
          navigate(-1);
          break;
        }
      case index.ClaimBuilderEvent.CLAIM_ITEM_DETAIL_STEP_DISMISSED:
        {
          navigate(ITEM_SELECTION);
          break;
        }
      case index.ClaimBuilderEvent.CLAIM_ITEM_DETAIL_UPDATED:
        {
          const itemClaimStep = currentItemClaim === null || currentItemClaim === void 0 ? void 0 : currentItemClaim.currentClaimStep;
          const completed = currentItemClaim === null || currentItemClaim === void 0 ? void 0 : currentItemClaim.complete;
          if (completed) {
            navigate(ITEM_SELECTION);
          } else {
            if (!itemClaimStep) throw new Error('No itemClaimStep defined. Cannot navigate');
            const nextItemClaimDetailPath = itemClaimStepToPath[itemClaimStep.id];
            navigate(nextItemClaimDetailPath);
          }
          break;
        }
      case index.ClaimBuilderEvent.CLAIM_PACKAGE_DETAIL_STEP_DISMISSED:
        {
          navigate(PACKAGE_SELECTION);
          break;
        }
      case index.ClaimBuilderEvent.CLAIM_PACKAGE_DETAIL_UPDATED:
        {
          navigate(PACKAGE_SELECTION);
          break;
        }
      case index.ClaimBuilderEvent.ITEM_CLAIM_SELECTION_COMPLETED:
        {
          navigate(ITEM_CLAIM_SUMMARY);
          break;
        }
      case index.ClaimBuilderEvent.PACKAGE_CLAIM_SELECTION_COMPLETED:
        {
          navigate(PACKAGE_CLAIM_SUMMARY);
          break;
        }
      case index.ClaimBuilderEvent.CLAIM_SUBMIT_COMPLETED:
        {
          navigate(CONFIRMATION);
          break;
        }
      case index.ClaimBuilderEvent.REMOVING_CLAIMED_ITEM:
        {
          navigate(ITEM_REMOVE_CONFIRMATION);
          break;
        }
      case index.ClaimBuilderEvent.CLAIMED_ITEM_REMOVE_CANCELED:
      case index.ClaimBuilderEvent.CLAIMED_ITEM_REMOVED:
        {
          navigate(ITEM_SELECTION);
          break;
        }
      case index.ClaimBuilderEvent.REMOVING_CLAIMED_PACKAGE:
        {
          navigate(PACKAGE_REMOVE_CONFIRMATION);
          break;
        }
      case index.ClaimBuilderEvent.CLAIMED_PACKAGE_REMOVE_CANCELED:
      case index.ClaimBuilderEvent.CLAIMED_PACKAGE_REMOVED:
        {
          navigate(PACKAGE_SELECTION);
          break;
        }
      case index.ClaimBuilderEvent.INVALID_DATA_PROVIDED:
        {
          navigate(ORDER_LOOKUP, {
            replace: true
          });
          break;
        }
      default:
        {
          throw new Error(`Received unknown event in ClaimNavigator: ${event}`);
        }
    }
  },
  // The missing dependency here is navigate. In the case of ClaimNavigator, event will first change, and then navigate will
  // change on the next render, which would cause every navigation to run twice. We can safely assume the navigate
  // function will have been udpated to what it needs to be before the effect is run again
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [state]);
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, children);
};

exports.ClaimNavigator = ClaimNavigator;
